/**
 * Cart Service
 * Handles shopping cart operations for purchasing digital products
 */
import apiClient from './apiClient';
import type { Product } from './productService';

export interface CartItem {
  productId: number;
  product: Product;
  quantity: number;
}

export interface Cart {
  items: CartItem[];
  total: number;
}

/**
 * Get the current user's cart
 */
export const getCart = async (): Promise<Cart> => {
  try {
    const response = await apiClient.get('/cart');
    return response.data;
  } catch (error) {
    console.error('Error fetching cart:', error);
    return { items: [], total: 0 };
  }
};

/**
 * Add a product to the cart
 */
export const addToCart = async (productId: number, quantity: number = 1): Promise<Cart> => {
  try {
    const response = await apiClient.post('/cart/items', { productId, quantity });
    return response.data;
  } catch (error) {
    console.error('Error adding item to cart:', error);
    throw new Error('Failed to add item to cart');
  }
};

/**
 * Remove a product from the cart
 */
export const removeFromCart = async (productId: number): Promise<Cart> => {
  try {
    const response = await apiClient.delete(`/cart/items/${productId}`);
    return response.data;
  } catch (error) {
    console.error('Error removing item from cart:', error);
    throw new Error('Failed to remove item from cart');
  }
};

/**
 * Update the quantity of a product in the cart
 */
export const updateCartItemQuantity = async (productId: number, quantity: number): Promise<Cart> => {
  try {
    const response = await apiClient.patch(`/cart/items/${productId}`, { quantity });
    return response.data;
  } catch (error) {
    console.error('Error updating cart item quantity:', error);
    throw new Error('Failed to update cart item quantity');
  }
};

/**
 * Clear the cart
 */
export const clearCart = async (): Promise<Cart> => {
  try {
    const response = await apiClient.delete('/cart');
    return response.data;
  } catch (error) {
    console.error('Error clearing cart:', error);
    throw new Error('Failed to clear cart');
  }
};

/**
 * Checkout and create an order
 */
export const checkout = async (): Promise<{ orderId: number }> => {
  try {
    const response = await apiClient.post('/orders');
    return response.data;
  } catch (error) {
    console.error('Error checking out:', error);
    throw new Error('Failed to complete checkout');
  }
};

/**
 * Get all items in the cart (legacy support)
 */
export const getCartItems = async (): Promise<CartItem[]> => {
  const cart = await getCart();
  return cart.items;
};

/**
 * Get the total number of items in cart
 */
export const getCartItemCount = async (): Promise<number> => {
  const cart = await getCart();
  return cart.items.reduce((total, item) => total + item.quantity, 0);
};

/**
 * Get the total price of all items in cart
 */
export const getCartTotal = async (): Promise<number> => {
  const cart = await getCart();
  return cart.total;
};
